import type { EnchantId } from '../core/enchant';
import type { ComponentId } from './components';

/**
 * Crafting recipes, available at Rest/Forge nodes.
 *
 * Cards define the piece; components and enchantments define what that piece
 * becomes. Each recipe spends accumulated components to produce a socketable
 * enchantment gem — the components' only purpose, and the reason a Rest node
 * is a decision rather than a heal button.
 */
export interface CraftRecipe {
  readonly id: string;
  readonly name: string;
  readonly result: EnchantId;
  readonly cost: Partial<Record<ComponentId, number>>;
  readonly description: string;
}

export const CRAFT_RECIPES: readonly CraftRecipe[] = [
  {
    id: 'craft_shatter',
    name: 'Craft Shatter Gem',
    result: 'shatter',
    cost: { masonry_fragment: 2, impact_core: 1 },
    description: '2× Masonry Fragment + 1× Impact Core → Shatter gem',
  },
  {
    id: 'craft_resonance',
    name: 'Craft Resonance Gem',
    result: 'resonance',
    cost: { prism_dust: 2, volt_component: 1 },
    description: '2× Prism Dust + 1× Volt Component → Resonance gem',
  },
  {
    id: 'craft_echo',
    name: 'Craft Echo Gem',
    result: 'echo',
    cost: { frost_crystal: 2, ember_fragment: 1 },
    description: '2× Frost Crystal + 1× Ember Fragment → Echo gem',
  },
  {
    id: 'craft_burn',
    name: 'Craft Burn Gem',
    result: 'burn',
    cost: { ember_fragment: 3, prism_dust: 1 },
    description: '3× Ember Fragment + 1× Prism Dust → Burn gem',
  },
];

/** Can the given component stock afford this recipe? */
export function canCraft(
  components: Partial<Record<ComponentId, number>>,
  recipe: CraftRecipe,
): boolean {
  for (const [comp, count] of Object.entries(recipe.cost)) {
    if ((components[comp as ComponentId] ?? 0) < (count ?? 0)) return false;
  }
  return true;
}

/** What the recipe costs minus what the player holds. Only deficits appear. */
export function missingFor(
  components: Partial<Record<ComponentId, number>>,
  recipe: CraftRecipe,
): Partial<Record<ComponentId, number>> {
  const missing: Partial<Record<ComponentId, number>> = {};
  for (const [comp, count] of Object.entries(recipe.cost)) {
    const id = comp as ComponentId;
    const deficit = (count ?? 0) - (components[id] ?? 0);
    if (deficit > 0) missing[id] = deficit;
  }
  return missing;
}
